import type { AssetRecord, BenchGraph, PublishedRecord, RecipeRecord, RunRecord } from './types';

const GRAPH_KEY = 'plus-workbench:v1:active-graph';
const RECIPES_KEY = 'plus-workbench:v1:recipes';
const RUNS_KEY = 'plus-workbench:v1:runs';
const ASSETS_KEY = 'plus-workbench:v1:assets';
const PUBLISHED_KEY = 'plus-workbench:v1:published';

function nowIso(): string {
  return new Date().toISOString();
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback;
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(key, JSON.stringify(value));
}

export class LocalBenchRepository {
  loadGraph(): BenchGraph | null {
    return readJson<BenchGraph | null>(GRAPH_KEY, null);
  }

  saveGraph(graph: BenchGraph): BenchGraph {
    const next = { ...graph, updatedAt: nowIso() };
    writeJson(GRAPH_KEY, next);
    return next;
  }

  listRecipes(): RecipeRecord[] {
    return readJson<RecipeRecord[]>(RECIPES_KEY, []);
  }

  saveRecipe(graph: BenchGraph): RecipeRecord {
    const recipes = this.listRecipes();
    const existing = recipes.find((recipe) => recipe.id === graph.id);
    const record: RecipeRecord = {
      id: graph.id,
      label: graph.label,
      graph,
      createdAt: existing?.createdAt ?? nowIso(),
      updatedAt: nowIso(),
    };
    writeJson(RECIPES_KEY, [record, ...recipes.filter((recipe) => recipe.id !== record.id)].slice(0, 20));
    return record;
  }

  listRuns(): RunRecord[] {
    return readJson<RunRecord[]>(RUNS_KEY, []);
  }

  saveRun(run: RunRecord): RunRecord {
    writeJson(RUNS_KEY, [run, ...this.listRuns()].slice(0, 50));
    return run;
  }

  listAssets(): AssetRecord[] {
    return readJson<AssetRecord[]>(ASSETS_KEY, []);
  }

  saveAssets(assets: AssetRecord[]): AssetRecord[] {
    const next = [...assets, ...this.listAssets()].slice(0, 100);
    writeJson(ASSETS_KEY, next);
    return next;
  }

  listPublished(): PublishedRecord[] {
    return readJson<PublishedRecord[]>(PUBLISHED_KEY, []);
  }

  savePublished(records: PublishedRecord[]): PublishedRecord[] {
    const next = [...records, ...this.listPublished()].slice(0, 50);
    writeJson(PUBLISHED_KEY, next);
    return next;
  }

  clear(): void {
    if (typeof localStorage === 'undefined') return;
    [GRAPH_KEY, RECIPES_KEY, RUNS_KEY, ASSETS_KEY, PUBLISHED_KEY].forEach((key) => localStorage.removeItem(key));
  }
}
